import * as React from "react";
import clsx from "clsx";
import { Info } from "lucide-react";
import { Switch } from "../../../ui/components/Switch";
import { Callout } from "../../../ui/components/Callout";
import { Text } from "../../../ui/components/Text";
import type { UserGlobalRole } from "../../lib/api/users";
import * as adminUsersApi from "../../lib/api/admin-users";
import { ConfirmModal } from "../../../ui/components/ConfirmModal";
import { ApiClientError } from "../../lib/api/client";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import { useI18n } from "../../lib/i18n";
import * as styles from "./UserDetailPage.css";

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiClientError || e instanceof ProApiClientError) {
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return fallback;
}

const SUPER_ADMIN_ROLE = "super-admin";

function formatRoleLabel(role: string): string {
  return role
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function sortGlobalRoles(
  roles: adminUsersApi.RoleSummary[],
): adminUsersApi.RoleSummary[] {
  return [...roles].sort((a, b) => {
    if (a.name === SUPER_ADMIN_ROLE) return -1;
    if (b.name === SUPER_ADMIN_ROLE) return 1;
    return a.name.localeCompare(b.name);
  });
}

export function GlobalRolesSection({
  userId,
  token,
  assignedRoles,
  onRolesUpdated,
  userLabel = "this user",
}: {
  userId: string;
  token: string;
  assignedRoles: UserGlobalRole[];
  onRolesUpdated: () => Promise<void>;
  userLabel?: string;
}) {
  const { t } = useI18n();
  const [allRoles, setAllRoles] = React.useState<adminUsersApi.RoleSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [savingRoleId, setSavingRoleId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = React.useState<{
    roleId: string;
    roleLabel: string;
    enabled: boolean;
  } | null>(null);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);

  const assignedIds = React.useMemo(
    () => new Set(assignedRoles.map((r) => r.id)),
    [assignedRoles],
  );

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminUsersApi
      .listGlobalRoles(token)
      .then((roles) => {
        if (!cancelled) {
          setAllRoles(sortGlobalRoles(roles));
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(getErrorMessage(e, "Failed to load roles"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleToggle = async (roleId: string, enabled: boolean) => {
    setSavingRoleId(roleId);
    setConfirmError(null);
    setError(null);
    const currentIds = assignedRoles.map((r) => r.id);
    const nextIds = enabled
      ? [...currentIds, roleId]
      : currentIds.filter((id) => id !== roleId);
    try {
      await adminUsersApi.updateUserRoles(token, userId, nextIds);
      await onRolesUpdated();
      setPendingToggle(null);
    } catch (e) {
      const message = getErrorMessage(e, "Failed to update roles");
      setConfirmError(message);
      setError(message);
    } finally {
      setSavingRoleId(null);
    }
  };

  const pendingCopy = pendingToggle
    ? pendingToggle.enabled
      ? {
          title: "Assign role?",
          body: `Assign the ${pendingToggle.roleLabel} global role to ${userLabel}?`,
          confirmLabel: "Assign role",
          confirmColor: "primary" as const,
        }
      : {
          title: "Remove role?",
          body: `Remove the ${pendingToggle.roleLabel} global role from ${userLabel}?`,
          confirmLabel: "Remove role",
          confirmColor: "danger" as const,
        }
    : null;

  if (loading) {
    return (
      <Text level="body-medium" style={{ color: "#6B7280" }}>
        Loading roles…
      </Text>
    );
  }

  return (
    <>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      )}
      {allRoles.length === 0 ? (
        <p className={styles.orgEmpty}>{t("pro.users.noGlobalRoles")}</p>
      ) : (
        <ul className={styles.globalRoleList}>
          {allRoles.map((role) => {
            const assigned = assignedIds.has(role.id);
            const busy = savingRoleId !== null;
            const label = formatRoleLabel(role.name);
            const isSuperAdmin = role.name === SUPER_ADMIN_ROLE;
            return (
              <li key={role.id} className={styles.globalRoleRow}>
                <div className={styles.globalRoleInfo}>
                  <div className={styles.globalRoleNameRow}>
                    <p
                      className={clsx(
                        styles.globalRoleName,
                        isSuperAdmin && styles.globalRoleNamePrimary,
                      )}
                    >
                      {label}
                    </p>
                    {role.description?.trim() && (
                      <span
                        className={styles.globalRoleInfoIcon}
                        title={role.description.trim()}
                        aria-label={role.description.trim()}
                      >
                        <Info size={14} aria-hidden />
                      </span>
                    )}
                  </div>
                </div>
                <Switch
                  label={`${label} role`}
                  hideLabel
                  checked={assigned}
                  disabled={busy}
                  onCheckedChange={(enabled) => {
                    if (enabled !== assigned && savingRoleId === null) {
                      setConfirmError(null);
                      setPendingToggle({
                        roleId: role.id,
                        roleLabel: label,
                        enabled,
                      });
                    }
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
      {pendingToggle && (
        <ConfirmModal
          open={Boolean(pendingToggle)}
          title={pendingCopy!.title}
          body={pendingCopy!.body}
          confirmLabel={pendingCopy!.confirmLabel}
          confirmColor={pendingCopy!.confirmColor}
          loading={savingRoleId !== null}
          error={confirmError}
          onCancel={() => {
            setPendingToggle(null);
            setConfirmError(null);
          }}
          onConfirm={() =>
            void handleToggle(pendingToggle.roleId, pendingToggle.enabled)
          }
        />
      )}
    </>
  );
}
